import { IOrganizationFeaturesResponse } from './../interfaces/organization-feature-response';
import { Injectable } from '@angular/core';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { Observable, of, pipe } from 'rxjs';
import { tap, take, switchMap, map } from 'rxjs/operators';
import { LogService } from './log.service';
import {
  CacheBuster,
  Cacheable,
  globalCacheBusterNotifier,
} from 'ts-cacheable';
import { AppDialogService, SaveMessages } from './app-dialog.service';
import { DropDown } from '../interfaces/drop-down.interface';
import { ProductFormComponent } from '../page/organization/components/organization-details/product-form.component';
import { CsoSearchResults } from '../components/organization-popup/models/cso-search-results.model';
import { IPasswordStrengthRulesResponse } from '../interfaces/passwordStrengthRulesResponse.interface';
import { OrganizationProductBulkUpdateComponent } from '../page/organization/components/organization-details/organization-product-bulk-update/organization-product-bulk-update.component';
import { ExplicitConfirmationComponent } from '../components/explicit-confirmation/explicit-confirmation.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrgTreeView } from '../shared/components/organization-tree-view/component/tree-view/tree-view.component';
import { BulkUpdateMoveStatusComponent } from '../page/organization/components/organization-details/organization-product-bulk-update/bulk-update-move-status.component';
import { IDeliveryEfficiencyForm } from '../components/delivery-efficiency-forecasting/IDeliveryEfficiencyForm';
import { IEmailAlertFormResponse } from '@interfaces/email-alerts-form';
import { TtlRequestCache } from '@utils/ttl-request-cache';
import {
  OrganizationGridRequest,
  OrganizationGridResult,
} from '../page/organization/models/organization-grid';

@Injectable()
export class OrganizationService {
  private readonly apiURL = 'Organization';
  private readonly orgFeaturesApiURL = 'organization-features';

  private readonly tapSaveOptions = {
    next: (response) => {
      this._ads.showSuccessMessage(SaveMessages.SaveSuccess);
    },
    error: (error) => {
      this._ads.showErrorMessage(SaveMessages.SaveError);
    },
  };

  isChangingCSO: boolean = false;

  // Backs getOrganization() below. Cleared by globalCacheBusterNotifier so a save (see
  // saveOrganization's @CacheBuster further down) doesn't leave a renamed org showing its old
  // name in a later title/read.
  private readonly organizationCache = new TtlRequestCache<number, IOrganizationDetailsObject>(
    null,
    globalCacheBusterNotifier,
  );

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private _ads: AppDialogService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}
  getBulkConfigTanks(organizationId: number): Observable<{ tankId: string; rtuId: string }[]> {
    return this.restClient.getDirect('TankBulkConfig/available-tanks/' + organizationId);
  }

  getBulkConfigRegions(organizationId: number, tankId?: string): Observable<DropDown<string>[]> {
    return this.restClient.getDirect<DropDown<string>[]>(
      tankId ? 'TankBulkConfig/regions-for-tank/' + encodeURIComponent(tankId)
        : 'TankBulkConfig/regions/' + organizationId);
  }

  @Cacheable()
  getParentOrganizationTree(orgId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/GetParentOrganizationTree/${orgId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          'Organization/GetParentOrganizationTree Response',
          response,
        ),
      ),
    );
  }
  /**
   * Deliberately NOT @Cacheable: the CSO chooser reopens against a list the user may have just
   * reorganised, and a session-long cache also meant every consumer shared one array instance.
   */
  getChildOrganizationList(orgId: number): Observable<CsoSearchResults[]> {
    return this.restClient.getAsModel<CsoSearchResults[]>(
      `${this.apiURL}/GetChildOrganizationList/${orgId}`,
    );
  }

  getCSOSearchResults(searchText: string, rootOrgId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/GetCSOSearchResults/${rootOrgId}?searchText=${encodeURIComponent(searchText)}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Organization/GetCSOSearchResults Response', response),
      ),
    );
  }
  /**
   * The Organizations grid. Replaced getOrganizationHierarchy and
   * getOrganizationHierarchyDirect, which hit two endpoints differing only in whether the whole
   * branch came back; that is `includeChildOrgs` on the request now. Both endpoints are gone.
   *
   * Deliberately NOT @Cacheable: the request carries the sort model, the filter model and the
   * requested row block, and ts-cacheable keys on the argument list, so a paged grid would serve
   * page 1's rows for page 2 on any repeat of the same arguments.
   */
  getOrganizationGrid(
    request: OrganizationGridRequest,
  ): Observable<OrganizationGridResult> {
    return this.restClient.postForResponse<OrganizationGridResult>(
      `${this.apiURL}/grid`,
      request,
    );
  }

  @Cacheable()
  getOrganizationDropDownByParentId(orgId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/drop-down-by-parent-org/${orgId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          `Organization/drop-down-by-parent-org/${orgId} Response`,
          response,
        ),
      ),
    );
  }




  @Cacheable()
  getOrganizationsForDropDownByParentOrgId(orgId: number): Observable<any> {
  if (!orgId) return of([]);

  const url = `${this.apiURL}/get-orgs-by-parent-org/${orgId}`;
  return this.restClient.postDirect<any>(url, {}).pipe(  // <-- send empty body
    tap((response) => {
      this.logger.log(url, response);
    })
  );
}


  /**
   * Just the billable org id. Callers that need only this must not pull GetOrganization, which
   * returns the whole org-admin payload (features, UST sites, FTP connections) to get at one int.
   */
  getBillableOrganizationId(orgId: number): Observable<number | null> {
    if (!orgId) return of(null);

    return this.restClient
      .getDirect<{ billableOrgId: number }>(`${this.apiURL}/${orgId}/billable-org`)
      .pipe(map((response) => response?.billableOrgId ?? null));
  }

  // Deliberately not a @CacheBuster: this is a read. `isInstant` fired the global notifier on
  // every call, wiping every bare @Cacheable() in the app - the grid query and column list
  // included - each time an org was read.
  //
  // Backed by organizationCache (TtlRequestCache, not @Cacheable, so the cache is inspectable
  // and clearable from code) so the page-title resolver and
  // OrganizationComponent/OrganizationDetailsComponent share one request instead of each
  // fetching independently.
  getOrganization(orgId: number): Observable<IOrganizationDetailsObject> {
    return this.organizationCache.get(orgId, () =>
      this.restClient.getDirect<IOrganizationDetailsObject>(
        `${this.apiURL}/GetOrganization/${orgId}`,
      ).pipe(
        tap((response) =>
          this.logger.log('Organization/GetOrganization Response', response),
        ),
      ),
    );
  }

  @CacheBuster({
    isInstant: true,
    cacheBusterNotifier: globalCacheBusterNotifier,
  })
  saveOrganization(org: any): Observable<any> {
    const apiResponse = this.restClient.post(
      `${this.apiURL}/SaveOrganization`,
      org,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Organization/SaveOrganization Response', response),
      ),
    );
  }

  getBillableOrganizationHierarchy(): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/GetBillableOrganizationHierarchy/`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          'Organization/GetBillableOrganizationHierarchy Response',
          response,
        ),
      ),
    );
  }

  getParentOrganizationDropDownList(
    orgId?: number,
    billableOrgId?: number,
    parentOrgId?: number,
  ): Observable<any> {
    const orgIdStr = orgId ? `${orgId}` : '';
    const billableOrgIdStr = billableOrgId ? `${billableOrgId}` : '';
    const parentOrgIdStr = parentOrgId ? `${parentOrgId}` : '';
    const url = `${this.apiURL}/getParentOrganizationDropDownList/${orgIdStr}?billableOrgId=${billableOrgIdStr}&parentOrgId=${parentOrgIdStr}`;

    const apiResponse = this.restClient.get(url);

    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          'Organization/getParentOrganizationDropDownList Response',
          response,
        ),
      ),
    );
  }

  getOrganizationDropDownByBillableOrg(
    billableOrgId: number,
  ): Observable<DropDown<number>[]> {
    const apiResponse = this.restClient.getDirect<DropDown<number>[]>(
      `${this.apiURL}/GetOrganizationDropDownByBillableOrg/${billableOrgId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          'Organization/GetOrganizationDropDownByBillableOrg Response',
          response,
        ),
      ),
    );
  }


  getMoveLocationOrganizationDropDown(
    billableOrgId: number,
  ): Observable<DropDown<number>[]> {
    const apiResponse = this.restClient.getDirect<DropDown<number>[]>(
      `${this.apiURL}/GetMoveLocationOrganizationDropDown/${billableOrgId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          'Organization/GetMoveLocationOrganizationDropDown Response',
          response,
        ),
      ),
    );
  }

  

  getOrganizationProductListDetails(
    organizationId: number,
  ): Observable<IOrganizationProductListVM> {
    return this.restClient.getDirect<IOrganizationProductListVM>(
      `${this.apiURL}/product-list/${organizationId}`,
    );
  }

  getOrganizationProductListDetailsPaged(
    organizationId: number,
    request: IOrganizationProductListPagedRequest,
  ): Observable<IOrganizationProductListPagedResponse> {
    return this.restClient.postDirect<IOrganizationProductListPagedResponse>(
      `${this.apiURL}/product-list-paged/${organizationId}`,
      request,
    );
  }

  getNonStandardTankCount(organizationId: number): Observable<number> {
    return this.restClient.getDirect<number>(
      `${this.apiURL}/non-standard-tank-count/${organizationId}`,
    );
  }

  getProductListForDfTank(
    organizationId: number,
  ): Observable<IOrganizationProductListItem[]> {
    return this.restClient.getDirect<IOrganizationProductListItem[]>(
      `${this.apiURL}/product-list-dftank/${organizationId}`,
    );
  }

  getBulkProductUpdateInfo(
    organizationId: number,
  ): Observable<IBulkProductUpdateVM> {
    return this.restClient.getDirect<IBulkProductUpdateVM>(
      `${this.apiURL}/bulk-product-update-info/${organizationId}`,
    );
  }

  getBulkProductUpdateMetadata(
    organizationId: number,
  ): Observable<IBulkProductUpdateMetadataResponse> {
    return this.restClient.getDirect<IBulkProductUpdateMetadataResponse>(
      `${this.apiURL}/bulk-product-update-metadata/${organizationId}`,
    );
  }

  getBulkProductUpdateInfoPaged(
    organizationId: number,
    request: IBulkProductUpdatePagedRequest,
  ): Observable<IBulkProductUpdatePagedResponse> {
    return this.restClient.postDirect<IBulkProductUpdatePagedResponse>(
      `${this.apiURL}/bulk-product-update-info-paged/${organizationId}`,
      request,
    );
  }

  showBulkUpdateConfirmationDialog(
    request: IBulkProductUpdateRequestVM,
  ): MatDialogRef<ExplicitConfirmationComponent> {
    return this.dialog.open(ExplicitConfirmationComponent, {
      panelClass: 'cso-dialog',
      data: {
        explicitConfirmationText:
          'OrganizationDetails.BulkUpdate.ConfirmationText',
        title: 'OrganizationDetails.BulkUpdate.BulkUpdateProductsTitle',
        messages: [
          'OrganizationDetails.BulkUpdate.ConfirmationMessage1',
          'OrganizationDetails.BulkUpdate.ConfirmationMessage2',
        ],
        cancelButtonText: 'Dialog.Cancel',
        confirmButtonText: 'OrganizationDetails.BulkUpdate.UpdateProductValues',
        inputLabelSuffix:
          'OrganizationDetails.BulkUpdate.ExplicityConfirmationLabelSuffix',
      },
    });
  }

  bulkProductsUpdate(request: IBulkProductUpdateRequestVM): Observable<any> {
    //guard clause
    if (
      !request ||
      !request.selectedProducts ||
      request.selectedProducts.length === 0
    )
      return of(null);

    //update the tanks
    return this.restClient
      .postForResponse<any>(`${this.apiURL}/bulk-products-update`, request)
      .pipe(
        tap({
          error: (response: any) => {
            this.logger.error(
              `organization-service.ts: updateBulkProducts($request)`,
            );
            this.showErrorMessage('Error updating the products...', response);
          },
        }),
      );
  }

  private showErrorMessage(message: string, response: any) {
    this.logger.log('Organization Error: ' + message, response);
    let duration = environment.snackbarDelayBeforeClose;
    let errors = [];

    //process the error response.
    // Model may change depending on the error type.
    // Attempt to handle all possible error responses.
    if (response.error && response.error.constructor === Array) {
      errors = response.error;
    } else if (
      response.error &&
      response.error.errors &&
      response.error.errors.constructor === Array
    ) {
      errors = response.error.errors;
    }

    if (errors.length > 0) {
      const timestamperrorsExist = response.error.find(
        (error: any) => error.errorNo > 0 && error.errorNo < 4,
      );

      if (timestamperrorsExist) {
        duration = environment.snackbarDelayBeforeClose * 2; //increase the duration for timestamp errors to allow the user time to read the message
        message += `\n ${this.translate.instant('General.TimestampError')}`;
      } else {
        response.error.forEach((error: any) => {
          message += `\n ${this.translate.instant(
            'OrganizationDetails.Error.' + error.stringResourceKey,
          )}`;
        });
      }
    }

    this.snackBar.open(message, null, {
      duration: duration,
      panelClass: ['snackbar-failure'],
    });
  }

  getPrimaryContactList(orgId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/Contactlist/${orgId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Organization/Contactlist Response', response),
      ),
    );
  }

  getInstallerMobileAppFeature(orgId: number): Observable<any> {
    let url = `${this.apiURL}/${orgId}/installer-mobile-app`;
    return this.restClient
      .get(url)
      .pipe(tap((response) => this.logger.log(url + ' Response', response)));
  }

  @Cacheable()
  getOrganizationFeatures(
    orgId: number,
  ): Observable<IOrganizationFeaturesResponse> {
    let url = `${this.orgFeaturesApiURL}/features/${orgId}`;
    let apiResponse =
      this.restClient.getDirect<IOrganizationFeaturesResponse>(url);
    return apiResponse.pipe(
      tap((response) => this.logger.log(url, response)),
      take(1),
    );
  }

  getOrganizationNotifications(orgId: number): Observable<any> {
    let url = `${this.apiURL}/notifications/${orgId}`;
    let apiResponse = this.restClient.getDirect(url);
    return apiResponse.pipe(
      tap((response) => this.logger.log(url, response)),
      take(1),
    );
  }

  getOrganizationProductsDropDown(
    orgId: number,
  ): Observable<DropDown<number>[]> {
    return of([]);
  }

  @Cacheable()
  getPasswordStrengthRequirements(
    orgId: number,
  ): Observable<IPasswordStrengthRulesResponse> {
    const apiResponse =
      this.restClient.getDirect<IPasswordStrengthRulesResponse>(
        `${this.apiURL}/${orgId}/password-strength-requirements`,
      );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          `organization/${orgId}/password-strength-requirements`,
          response,
        ),
      ),
    );
  }

  saveInstallerMobileAppFeature(
    orgId: number,
    formVM: IInstallerMobileAppFeature,
  ): Observable<any> {
    this._ads.showGeneralMessage(SaveMessages.Saving);
    return this.restClient
      .post(`${this.apiURL}/${orgId}/installer-mobile-app`, formVM)
      .pipe(tap(this.tapSaveOptions), take(1));
  }

  @CacheBuster({
    isInstant: true,
  })
  saveAlarms(orgId: number, alarms: object[]): Observable<any> {
    this._ads.showGeneralMessage(SaveMessages.Saving);
    const payload = {
      alarmDefinitions: alarms,
    };
    return this.restClient.post(`${this.apiURL}/${orgId}/alarm`, payload)
      .pipe(
        tap(this.tapSaveOptions),
        take(1),
      );
  }

  setOrganizationNotifications(
    orgId: number,
    model: IEmailAlertNotification[],
  ): Observable<any> {
    let url = `${this.apiURL}/notifications/${orgId}`;
    let apiResponse = this.restClient.post(url, model);
    return apiResponse.pipe(
      tap((response) => this.logger.log(url, response)),
      take(1),
    );
  }

  updateOrgProducts(
    orgId: number,
    products: any[],
  ): Observable<IOrganizationProductListItem[]> {
    const productCount = products.length;

    return this._ads
      .showConfirmationDialog(
        'Update Product(s)',
        'Updating the product list will affect ' +
          productCount +
          ' product(s). Are you sure you want to continue?',
      )
      .pipe(
        switchMap((result) => {
          if (!result) return of([]);
          const url = `${this.apiURL}/product-list-active-state/${orgId}`;
          return this.restClient
            .postForResponse<IOrganizationProductListItem[]>(url, products)
            .pipe(tap(this.tapSaveOptions));
        }),
      );
  }

  showMoveStatusDialog(
    selectedProducts: any[],
  ): MatDialogRef<BulkUpdateMoveStatusComponent, { sendToList: boolean }> {
    return this.dialog.open(BulkUpdateMoveStatusComponent, {
      panelClass: 'cso-dialog',
      width: '85%',
      data: { results: selectedProducts, showToListButton: true },
    });
  }

  saveProduct(
    orgId: number,
    product: IOrganizationProductListItem,
  ): Observable<IOrganizationProductListItem> {
    this._ads.showGeneralMessage(SaveMessages.Saving);
    const url = `${this.apiURL}/${orgId}/product`;
    return this.restClient
      .postForResponse<IOrganizationProductListItem>(url, product)
      .pipe(tap(this.tapSaveOptions));
  }

  getDeliveryEfficiency(orgId: number): Observable<IDeliveryEfficiencyForm> {
    const url = `${this.apiURL}/${orgId}/delivery-efficiency`;
    return this.restClient.getAsModel<IDeliveryEfficiencyForm>(url);
  }

  setDeliveryEfficiency(
    orgId: number,
    model: IDeliveryEfficiencyForm,
  ): Observable<void> {
    const url = `${this.apiURL}/${orgId}/delivery-efficiency`;
    this._ads.showGeneralMessage(SaveMessages.Saving);
    return this.restClient.post(url, model).pipe(tap(this.tapSaveOptions));
  }

  /**
   * What a pending parent change means for the organization's affiliation. Read-only - the endpoint
   * writes nothing, it only reports whether keeping the affiliation is still legitimate.
   */
  getParentChangeAffiliationPreview(
    orgId: number,
    newParentOrgId: number,
  ): Observable<IParentChangeAffiliationPreview> {
    const url = `${this.apiURL}/${orgId}/parent-change-affiliation-preview/${newParentOrgId}`;
    return this.restClient.getAsModel<IParentChangeAffiliationPreview>(url);
  }

  getParticipationCascadePreview(
    orgId: number,
    participates: boolean,
  ): Observable<IParticipationCascadePreview> {
    const url = `${this.apiURL}/${orgId}/participation-cascade-preview/${participates}`;
    return this.restClient.getAsModel<IParticipationCascadePreview>(url);
  }

  getAffiliatedOrgList(orgId: number): Observable<IAffiliateOrgPicker> {
    const url = `${this.apiURL}/${orgId}/get-affiliated-orgs-list`;
    return this.restClient.getAsModel<IAffiliateOrgPicker>(url);
  }

  getAffiliationDomain(orgId: number): Observable<IAffiliationDomainItem[]> {
    const url = `${this.apiURL}/${orgId}/affiliate-orgs`;
    return this.restClient.getAsModel<IAffiliationDomainItem[]>(url);
  }

  /**
   * Who shares INTO this organization's domain. Superuser only, enforced on the server - the
   * endpoint carries RequireSuperUser and the service re-checks. Call it only when the client
   * already knows the user is one, or it answers 403.
   */
  getAffiliationInbound(orgId: number): Observable<IAffiliationInboundItem[]> {
    const url = `${this.apiURL}/${orgId}/affiliation-inbound`;
    return this.restClient.getAsModel<IAffiliationInboundItem[]>(url);
  }

  showAddOrUpdateProductFormDialog(
    orgId: number,
    product: IOrganizationProductListItem | null,
  ): Observable<IOrganizationProductListItem> {
    product = product ?? {
      productId: 0,
      prodDesc: '',
      coeffExp: 0.0005,
      userProductNumber: '-1',
      createdBy: '',
      createdOn: '',
      modifiedBy: '',
      modifiedOn: '',
      active: true,
      tankCount: 0,
      productSGU: 1,
    };

    return this._ads.showDialog(ProductFormComponent, {
      product: product,
      orgId: orgId,
    });
  }

  showBulkUpdateList(
    orgId: number,
  ): MatDialogRef<OrganizationProductBulkUpdateComponent, { orgId: number }> {
    return this._ads.showDialogReturnRef(
      OrganizationProductBulkUpdateComponent,
      { orgId: orgId },
    );
  }

  showProductHasTanksDialog(translatedMessage: string) {
    this._ads.showGeneralDialog('', translatedMessage);
  }

  emailExistsAlreadyInDb(
    emailAddress: string,
    orgId: number,
  ): Observable<number> {
    const url = `${this.apiURL}/${orgId}/alarm/verify-email`;
    return this.restClient
      .postForResponse<number>(url, { emailAddress: emailAddress })
      .pipe(
        tap((response) => {
          this.logger.log('Email Exists Already In DB', response);
        }),
      );
  }

  getAlarmDefinitions(orgId: number): Observable<IEmailAlertFormResponse> {
    const url = `${this.apiURL}/${orgId}/alarm`;
    return this.restClient.getAsModel<IEmailAlertFormResponse>(url);
  }
}

/**
 * What switching "Participates in Affiliation" would do. Turning it off resets the organization - its
 * tanks lose their affiliation - so their count is given for any organization. Only the root of a
 * billable organization's scope may apply the change to child organizations, so `children` is empty
 * anywhere else.
 */
export interface IParticipationCascadePreview {
  organizationId: number;
  participates: boolean;
  isBillableOrganization: boolean;

  /** Tanks in this organization that would lose their affiliation. Zero when turning participation on. */
  organizationAffiliatedTankCount: number;

  /**
   * Tanks in OTHER organizations affiliated to this one, which also lose that affiliation when
   * participation is switched off. Usually in another billable: switching off has to stop other
   * organizations affiliating to you, not only stop you affiliating to them.
   */
  organizationInboundTankCount: number;

  affectedChildCount: number;

  /**
   * The organizations behind affectedChildCount, so the change can be narrowed to a subset. Each carries
   * its own tank count, so the warning can total whatever the user picks.
   */
  children: IParticipationCascadeChild[];

  /**
   * Organizations between those children and this one that are not themselves changing. Shown greyed
   * in the picker so it reads as the real hierarchy; never applied to.
   */
  structuralOrganizations: IParticipationCascadeChild[];
}

export interface IParticipationCascadeChild {
  organizationId: number;
  organizationName: string;

  /** The parent, when the picker shows it. Null for a direct child of the billable organization. */
  parentOrganizationId: number | null;

  /** Tanks in other organizations affiliated to THIS child, which also lose their affiliation. */
  inboundTankCount: number;

  /** Tanks that would lose their affiliation if the change is applied to it. */
  affiliatedTankCount: number;
}

/**
 * The affiliate picker's payload. One list, two views: "Show all" re-roots the tree at everything the
 * caller may reach, and unchecked it roots at defaultRootOrgId - the subject's billable organization.
 * Both are subtrees of the same list, so the toggle costs no round trip.
 */
export interface IAffiliateOrgPicker {
  organizations: OrgTreeView[];
  defaultRootOrgId: number;
}

export interface IEmailAlertNotificationResponse {
  emailAlertNotifications: IEmailAlertNotification[];
}

export interface IEmailAlertNotification {
  alarmBitsExtendedEntityId: number;
  description: string;
  userId: number;
  extraordinaryStopUsageEnabled: boolean;
  extraordinaryDailyUsageEnabled: boolean;
  active: boolean;
  alertIsNew: boolean;
}

export interface IInstallerMobileAppFeature {
  orgId: number;
  isInstallerMobileAppEnabled: boolean;
  newInstallerAuthType: NewInstallAuthTypes;
  newInstallerCanAssignLocation: boolean;
  newInstallerCanCreateLocation: boolean;
}

export enum NewInstallAuthTypes {
  TemporaryAuthentication = 1,
  DoesNotRequireAdminApproval = 2,
  RequiresAdminApproval = 3,
}

export interface IOrganizationProductListVM {
  orgHasCustomProductList: boolean;
  organizationProductList: IOrganizationProductListItem[];
  nonStdTankCount: number;
}
export interface IOrganizationProductListItem {
  productId: number;
  prodDesc: string;
  coeffExp: number;
  userProductNumber: string;
  createdBy: string;
  createdOn: string;
  modifiedBy: string;
  modifiedOn: string;
  active: boolean;
  tankCount: number;
  productSGU: number;
}

export interface IOrganizationProductListPagedRequest {
  pageNumber: number;
  pageSize: number;
  searchText?: string;
  includeInactive: boolean;
}

export interface IOrganizationProductListPagedResponse {
  orgHasCustomProductList: boolean;
  organizationProductList: IOrganizationProductListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface IBulkProductUpdateVM {
  orgAvailableProductList: IOrganizationProductListItem[];
  orgCandidateProductList: IOrgCandidateProductListItem[];
  totalTankCount: number;
}

export interface IBulkProductUpdatePagedRequest {
  pageNumber: number;
  pageSize: number;
  searchText?: string;
}

export interface IBulkProductUpdateMetadataResponse {
  orgAvailableProductList: IOrganizationProductListItem[];
}

export interface IBulkProductUpdatePagedResponse {
  orgCandidateProductList: IOrgCandidateProductListItem[];
  totalCandidateProducts: number;
  /** Tanks needing updates across the whole org - not narrowed by searchText or the current page. */
  totalTankCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface IBulkProductUpdateRequestVM {
  selectedProductId: IOrganizationProductListItem[];
  selectedProducts: IOrgCandidateProductListItem[];
  orgId: number;
}

export interface IOrgCandidateProductListItem {
  selected: boolean;
  prodDesc: string;
  productSGU: number;
  userProductNumber: string;
  tankCount: number;
  recommendedProductDesc: string;
  recommendedProductId: number;
}

export interface IOrganizationDetailsObject {
  organizationDetails: IOrganizationDetails;
  alarmListDTO: IAlarmList[];
  uSTSiteViewDTO: IUSTSiteView[];
  dataFeedFTPConnectionViewDTO: IDataFeedFTPConnectionView[];
  features: IFeature[];
  errorCode: number;
}

export interface IOrganizationDetails {
  organizationID: number;
  organizationName: string;
  parentOrganizationID: number;
  billableOrgID: number;
  userCustId: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  primaryContactID: number;
  active: boolean;
  parentOrganizationName: string;
  deliveryCost: number;
  targetDeliveryPercent: number;
  activeDaysMask: number;
  isBillableOrg: boolean;
  organizationGuid: string;
  hasCustomProductList: boolean;
  organizationMatchText: string;
  affiliatedOrganizationId: number;
  affiliatedOrganizationName: string;
  themeId: number;
  orgProviderId: number;
  subProviderId: number;
  useGlobal: boolean;
  isVar: boolean;
  isOem: boolean;
  isAgent: boolean;
  hasAffiliatedOrganizations: boolean;
  hasOrdersShipments: boolean;
}

export interface IAlarmList {
  alarmID: number;
  organizationID: number;
  shortNotification: boolean;
  normalNotification: boolean;
  includeAttachment: boolean;
  includeLink: boolean;
  criticalLowAlarm: boolean;
  lowAlarm: boolean;
  highAlarm: boolean;
  criticalHighAlarm: boolean;
  lowTempAlarm: boolean;
  highTempAlarm: boolean;
  lowBatteryAlarm: boolean;
  fillDetect: boolean;
  shortFillDetect: boolean;
  expectedCallAlarm: boolean;
  aCPowerFailure: boolean;
  active: boolean;
}

export interface IUSTSiteView {
  uSTSiteID: number;
  organizationID: number;
  siteName: string;
  uSTState: number;
  lastUpdateTime: string;
  activeTanks: number;
  newTanks: number;
  ignoreTanks: number;
}

export interface IDataFeedFTPConnectionView {
  fTPSiteID: number;
  organizationID: number;
  dFConnectionName: string;
  uRL: string;
  dFState: number;
  lastUpdateTime: string;
  activeTanks: number;
  newTanks: number;
  requestCreate: number;
  requestDelete: number;
}

export interface IFeature {
  featureId: number;
  featureName: string;
  isPremier: number;
  active: boolean;
  organizationId: number | null;
  featureIcon: string;
}
/**
 * Mirrors AffiliationDomainItemDTO. One row per organization in the subject's domain - itself and
 * every descendant inside the same billable - for the summary grid on Organization Details.
 *
 * Outbound only. Nothing here says who is affiliated TO these organizations.
 */
export interface IAffiliationDomainItem {
  organizationId: number;
  organizationName: string;
  parentOrganizationId: number;
  isBillableOrganization: boolean;
  participatesInAffiliation: boolean;
  affiliatedOrganizationId: number;
  /** Null with no destination, and also when the destination id does not resolve to an organization. */
  affiliatedOrganizationName: string | null;
  affiliatePointsOutOfBillable: boolean;
  tankCount: number;
  sharedTankCount: number;
  /**
   * Tanks disagreeing with this organization's default - what changing the destination destroys.
   * See rule 1 and rule 2 in docs/affiliate-organization-feature.md.
   */
  exceptionTankCount: number;
  isRemnant: boolean;
}

/**
 * Mirrors AffiliationInboundItemDTO. One organization sharing tanks INTO the subject's domain.
 *
 * The destination's side of a link, which every customer-facing read in the product deliberately
 * cannot see - see affiliate-organization-feature.md §1. Superuser only.
 */
export interface IAffiliationInboundItem {
  organizationId: number;
  organizationName: string;
  billableOrganizationId: number;
  billableOrganizationName: string | null;
  /** False for a source reaching the domain only through tanks overridden one at a time. */
  autoValuePointsIntoDomain: boolean;
  tanksSharedIntoDomain: number;
  crossesBillableBoundary: boolean;
}

/**
 * Mirrors ParentChangeAffiliationPreviewDTO. Changing a parent does not move affiliation by itself;
 * this reports whether KEEPING it is still legitimate, so the prompt can offer retain-or-clear.
 */
export interface IParentChangeAffiliationPreview {
  organizationId: number;
  newParentOrganizationId: number;
  currentAffiliateOrgId: number;
  currentAffiliateName: string;
  hasAffiliation: boolean;
  canRetain: boolean;
  retainBlockedReason: string;
  tanksAffectedByClearing: number;
}
